import Papa from 'papaparse';
import DsoTools from '@/lib/es-bill-shared-js/misc/DsoTools';
import {
  formatDate,
  isDateValid,
} from '@/lib/es-bill-shared-js/misc/timeTools';
import { getFilteredIssueResults } from '@/services/billrunEnergyDataInterpretationService';
import {
  filterDetailRows,
  formatDateRangesForDisplay,
  normalizeToLocalMidnight,
} from '@/services/billrunFiltersService';
import { billRunAnalysisKey } from '@/stores/billrunsStore';

export const PROBLEM_TYPE_ORDER = ['l3', 'incorrectQh', 'missingData', 'surplus'];

const PROBLEM_TYPE_I18N_KEY = {
  l3: 'problemTypeL3',
  incorrectQh: 'problemTypeIncorrectQh',
  missingData: 'problemTypeMissingData',
  surplus: 'problemTypeSurplus',
};

export const CSV_PROBLEM_TYPE_LABELS = {
  l3: 'L3 Werte',
  incorrectQh: 'Fehlerhafte Viertelstunden',
  missingData: 'Fehlende Daten',
  surplus: 'Zusätzliche Daten',
};

const CSV_HEADERS_FIXED = [
  'Netzbetreiber',
  'Gemeinschaft',
  'Zählpunkt',
  'Problem Typ',
  'Zählpunkt Codes',
  'Last Update',
];

const DATE_ONLY_PROBLEM_TYPES = [
  { problemType: 'l3', entries: (filtered) => filtered.uncleanMeteringPointsDetailResults },
  { problemType: 'incorrectQh', entries: (filtered) => filtered.incorrectCountOfQhResults },
];

/**
 * Whether a billrun contributes detail rows. Test runs always included; otherwise only
 * non-billable runs without an energy-sum-deviation badge.
 *
 * @param {object} billrun
 * @param {import('@/services/billrunEnergyDataInterpretationService').EnergyDataInterpretation} interpretation
 * @returns {boolean}
 */
export function shouldIncludeBillrun(billrun, interpretation) {
  if (billrun.testRun) {
    return true;
  }
  return !interpretation.isBillable && !interpretation.showSumDeviationBadge;
}

/**
 * Parse an analysis issue date to local midnight; invalid or missing values return null.
 *
 * @param {unknown} dateValue
 * @returns {Date|null}
 */
function parseIssueDate(dateValue) {
  if (dateValue == null) {
    return null;
  }
  const d = normalizeToLocalMidnight(new Date(dateValue));
  return isDateValid(d) ? d : null;
}

/**
 * Parse dateLastUpdate as a raw timestamp (no midnight normalization); invalid → null.
 *
 * @param {unknown} dateValue
 * @returns {Date|null}
 */
function parseLastUpdate(dateValue) {
  if (dateValue == null) {
    return null;
  }
  const d = new Date(dateValue);
  return isDateValid(d) ? d : null;
}

/**
 * Dedupe issue dates by timestamp, merge consecutive days into ranges, then format each.
 *
 * @param {Date[]} dates
 * @returns {string[]}
 */
function datesToFormattedRanges(dates) {
  return formatDateRangesForDisplay(dates);
}

/**
 * Resolve DSO from the metering-point prefix (first 8 chars). On failure, records the
 * prefix in dsoWarnings and returns a fallback "unknown DSO" display label.
 *
 * @param {object|null|undefined} community
 * @param {string} meteringPoint
 * @param {Set<string>} dsoWarnings
 * @returns {{ ecnDso: string, dsoDisplayFull: string, usedFallback: boolean }}
 */
function resolveDso(community, meteringPoint, dsoWarnings) {
  const prefix = meteringPoint.substring(0, 8);
  try {
    if (!community) {
      throw new Error('community missing');
    }
    const ecnDso = DsoTools.detectDso(community, prefix);
    const title = DsoTools.getDsoByEcn(ecnDso).title;
    return {
      ecnDso,
      dsoDisplayFull: `${ecnDso} - ${title}`,
      usedFallback: false,
    };
  } catch {
    dsoWarnings.add(prefix);
    return {
      ecnDso: prefix,
      dsoDisplayFull: `${prefix} - unbekannter Netzbetreiber`,
      usedFallback: true,
    };
  }
}

/**
 * Intermediate row before date-range formatting and group-display assignment.
 *
 * @typedef {Object} PartialDetailRow
 * @property {string} ecid
 * @property {string} ecn
 * @property {string} communityName
 * @property {string} ecnDso
 * @property {string} dsoDisplayFull
 * @property {string} communityDisplayFull
 * @property {string} meteringPoint
 * @property {string} problemType
 * @property {string} meterCodes
 * @property {Date[]} dates
 * @property {Date|null} lastUpdateDate
 */

/**
 * Merge source into target: union dates (deduped by timestamp), keep latest lastUpdateDate,
 * copy meterCodes only when target has none (first wins).
 *
 * @param {PartialDetailRow} target
 * @param {PartialDetailRow} source
 */
function mergePartialRow(target, source) {
  const dateMap = new Map(target.dates.map((d) => [d.getTime(), d]));
  for (const d of source.dates) {
    dateMap.set(d.getTime(), d);
  }
  target.dates = [...dateMap.values()];

  if (source.lastUpdateDate) {
    if (!target.lastUpdateDate || source.lastUpdateDate > target.lastUpdateDate) {
      target.lastUpdateDate = source.lastUpdateDate;
    }
  }

  if (source.meterCodes && !target.meterCodes) {
    target.meterCodes = source.meterCodes;
  }
}

/**
 * Insert a partial row or merge into an existing one keyed by ecid:meteringPoint:problemType.
 *
 * @param {Map<string, PartialDetailRow>} rowMap
 * @param {PartialDetailRow} row
 */
function upsertPartialRow(rowMap, row) {
  const key = `${row.ecid}:${row.meteringPoint}:${row.problemType}`;
  const existing = rowMap.get(key);
  if (existing) {
    mergePartialRow(existing, row);
    return;
  }
  rowMap.set(key, row);
}

/**
 * Group analysis entries by meteringPoint; collect issue dates and last-update timestamps
 * per MP. Map iteration order follows first-seen MP in entries.
 *
 * @param {Array} entries
 * @param {(entry: object) => boolean} [filter]
 * @returns {Map<string, { dates: Date[], lastUpdates: Date[] }>}
 */
function collectDatesByMeterPoint(entries, filter = () => true) {
  const byMp = new Map();
  for (const entry of entries) {
    if (!filter(entry)) {
      continue;
    }
    const mp = entry?.meteringPoint;
    if (typeof mp !== 'string' || mp.length === 0) {
      continue;
    }
    if (!byMp.has(mp)) {
      byMp.set(mp, { dates: [], lastUpdates: [] });
    }
    const bucket = byMp.get(mp);
    const issueDate = parseIssueDate(entry.date);
    if (issueDate) {
      bucket.dates.push(issueDate);
    }
    const lastUpdate = parseLastUpdate(entry.dateLastUpdate);
    if (lastUpdate) {
      bucket.lastUpdates.push(lastUpdate);
    }
  }
  return byMp;
}

/**
 * CSV column value for meter codes: "prefix: code1, code2" with deduped codes; empty → ''.
 *
 * @param {string[]} codes
 * @param {string} prefix
 * @returns {string}
 */
function formatMeterCodesColumn(codes, prefix) {
  if (!Array.isArray(codes) || codes.length === 0) {
    return '';
  }
  const unique = [...new Set(codes)];
  return `${prefix}: ${unique.join(', ')}`;
}

/**
 * Return the Date with the latest timestamp; empty array → null.
 *
 * @param {Date[]} dates
 * @returns {Date|null}
 */
function latestFromDates(dates) {
  if (dates.length === 0) {
    return null;
  }
  return new Date(Math.max(...dates.map((d) => d.getTime())));
}

/**
 * Shared billrun/community labels reused when building partial rows for one billrun.
 *
 * @typedef {Object} BillrunContext
 * @property {string} ecid
 * @property {string} ecn
 * @property {string} communityName
 * @property {string} communityDisplayFull
 */

/**
 * Per-metering-point aggregation of codes, issue dates, and last-update timestamps.
 *
 * @typedef {{ codes: Set<string>, dates: Date[], lastUpdates: Date[] }} MeterPointBucket
 */

/**
 * Empty map for aggregating surplus/missing issues keyed by meteringPoint.
 *
 * @returns {Map<string, MeterPointBucket>}
 */
function createMeterPointBucketMap() {
  return new Map();
}

/**
 * Add codes (Set-deduped), issue date, and last update to a metering-point bucket;
 * creates the bucket on first sighting of mp.
 *
 * @param {Map<string, MeterPointBucket>} byMp
 * @param {string} mp
 * @param {string[]} codes
 * @param {Date|null} issueDate
 * @param {Date|null} lastUpdate
 */
function accumulateMeterPointIssue(byMp, mp, codes, issueDate, lastUpdate) {
  if (!byMp.has(mp)) {
    byMp.set(mp, { codes: new Set(), dates: [], lastUpdates: [] });
  }
  const bucket = byMp.get(mp);
  for (const code of codes) {
    bucket.codes.add(code);
  }
  if (issueDate) {
    bucket.dates.push(issueDate);
  }
  if (lastUpdate) {
    bucket.lastUpdates.push(lastUpdate);
  }
}

/**
 * Build one PartialDetailRow: resolve DSO, apply billrun context, derive lastUpdateDate
 * from the latest entry in issue.lastUpdates.
 *
 * @param {BillrunContext} billrunContext
 * @param {object|null|undefined} community
 * @param {Set<string>} dsoWarnings
 * @param {{
 *   meteringPoint: string,
 *   problemType: string,
 *   meterCodes?: string,
 *   dates: Date[],
 *   lastUpdates: Date[],
 * }} issue
 * @returns {PartialDetailRow}
 */
function createPartialDetailRow(billrunContext, community, dsoWarnings, issue) {
  const dso = resolveDso(community, issue.meteringPoint, dsoWarnings);
  return {
    ecid: billrunContext.ecid,
    ecn: billrunContext.ecn,
    communityName: billrunContext.communityName,
    ecnDso: dso.ecnDso,
    dsoDisplayFull: dso.dsoDisplayFull,
    communityDisplayFull: billrunContext.communityDisplayFull,
    meteringPoint: issue.meteringPoint,
    problemType: issue.problemType,
    meterCodes: issue.meterCodes ?? '',
    dates: issue.dates,
    lastUpdateDate: latestFromDates(issue.lastUpdates),
  };
}

/**
 * Produce detail rows for one billrun from filtered analysis. L3/QH from date-only issues;
 * surplus/missing from meter-code issues. Emission order: l3 → incorrectQh → surplus → missingData.
 *
 * @param {object} billrun
 * @param {object|null} analysis
 * @param {object|null|undefined} community
 * @param {boolean} billingRelevantOnly
 * @param {Set<string>} dsoWarnings
 * @returns {PartialDetailRow[]}
 */
function buildPartialRowsForBillrun(billrun, analysis, community, billingRelevantOnly, dsoWarnings) {
  const options = { billingRelevantOnly };
  const filtered = getFilteredIssueResults(analysis, options);
  const communityName = community?.name ?? billrun.ecid;
  const billrunContext = {
    ecid: billrun.ecid,
    ecn: billrun.ecn,
    communityName,
    communityDisplayFull: `${billrun.ecn} - ${communityName}`,
  };
  const rows = [];

  for (const { problemType, entries } of DATE_ONLY_PROBLEM_TYPES) {
    const byMp = collectDatesByMeterPoint(entries(filtered));
    for (const [meteringPoint, { dates, lastUpdates }] of byMp) {
      rows.push(createPartialDetailRow(billrunContext, community, dsoWarnings, {
        meteringPoint,
        problemType,
        dates,
        lastUpdates,
      }));
    }
  }

  const surplusByMp = createMeterPointBucketMap();
  const missingByMp = createMeterPointBucketMap();

  for (const entry of filtered.incorrectMeterCodesResults) {
    const mp = entry?.meteringPoint;
    if (typeof mp !== 'string' || mp.length === 0) {
      continue;
    }
    const issueDate = parseIssueDate(entry.date);
    const lastUpdate = parseLastUpdate(entry.dateLastUpdate);

    if (Array.isArray(entry.meterCodesSurplus) && entry.meterCodesSurplus.length > 0) {
      accumulateMeterPointIssue(surplusByMp, mp, entry.meterCodesSurplus, issueDate, lastUpdate);
    }

    if (Array.isArray(entry.meterCodesMissing) && entry.meterCodesMissing.length > 0) {
      accumulateMeterPointIssue(missingByMp, mp, entry.meterCodesMissing, issueDate, lastUpdate);
    }
  }

  for (const { problemType, codePrefix, byMp } of [
    { problemType: 'surplus', codePrefix: 'surplus', byMp: surplusByMp },
    { problemType: 'missingData', codePrefix: 'missing', byMp: missingByMp },
  ]) {
    for (const [meteringPoint, { codes, dates, lastUpdates }] of byMp) {
      rows.push(createPartialDetailRow(billrunContext, community, dsoWarnings, {
        meteringPoint,
        problemType,
        meterCodes: formatMeterCodesColumn([...codes], codePrefix),
        dates,
        lastUpdates,
      }));
    }
  }

  return rows;
}

/**
 * Convert partial rows to display rows: formatted date ranges, lastUpdate string, i18n key,
 * and empty dsoDisplay/communityDisplay placeholders for later grouping.
 *
 * @param {PartialDetailRow[]} partialRows
 * @returns {object[]}
 */
function finalizeRows(partialRows) {
  return partialRows.map((row) => ({
    ecid: row.ecid,
    ecn: row.ecn,
    ecnDso: row.ecnDso,
    dsoDisplayFull: row.dsoDisplayFull,
    communityDisplayFull: row.communityDisplayFull,
    meteringPoint: row.meteringPoint,
    problemType: row.problemType,
    problemTypeI18nKey: PROBLEM_TYPE_I18N_KEY[row.problemType],
    meterCodes: row.meterCodes,
    lastUpdate: row.lastUpdateDate ? formatDate(row.lastUpdateDate) : '',
    dateRanges: datesToFormattedRanges(row.dates),
    dsoDisplay: '',
    communityDisplay: '',
  }));
}

/**
 * Fill dsoDisplay/communityDisplay only on the first row of each DSO/community group.
 * Expects rows pre-sorted by DSO and community (see sortDetailRows).
 *
 * @param {object[]} rows
 * @returns {object[]}
 */
export function applyGroupDisplays(rows) {
  let lastDso = null;
  let lastCommunityKey = null;
  return rows.map((row) => {
    const communityKey = `${row.ecnDso}:${row.ecid}`;
    const dsoDisplay = row.ecnDso !== lastDso ? row.dsoDisplayFull : '';
    const communityDisplay = communityKey !== lastCommunityKey ? row.communityDisplayFull : '';
    lastDso = row.ecnDso;
    lastCommunityKey = communityKey;
    return { ...row, dsoDisplay, communityDisplay };
  });
}

/**
 * Sort rows by ecnDso, ecn, meteringPoint, then PROBLEM_TYPE_ORDER.
 *
 * @param {object[]} rows
 * @returns {object[]}
 */
function sortDetailRows(rows) {
  const problemTypeIndex = Object.fromEntries(PROBLEM_TYPE_ORDER.map((t, i) => [t, i]));
  return [...rows].sort((a, b) => {
    const dsoCmp = String(a.ecnDso).localeCompare(String(b.ecnDso), undefined, { numeric: true });
    if (dsoCmp !== 0) {
      return dsoCmp;
    }
    const ecnCmp = String(a.ecn).localeCompare(String(b.ecn), undefined, { numeric: true });
    if (ecnCmp !== 0) {
      return ecnCmp;
    }
    const mpCmp = String(a.meteringPoint).localeCompare(String(b.meteringPoint));
    if (mpCmp !== 0) {
      return mpCmp;
    }
    return (problemTypeIndex[a.problemType] ?? 99) - (problemTypeIndex[b.problemType] ?? 99);
  });
}

/**
 * Inputs for buildDetailRows: billruns plus keyed analysis, interpretation, and communities.
 *
 * @typedef {Object} BuildDetailRowsInput
 * @property {Array} billruns
 * @property {Record<string, object|null>} analysisByBillRunKey
 * @property {Record<string, import('@/services/billrunEnergyDataInterpretationService').EnergyDataInterpretation>} interpretationByBillRunKey
 * @property {Map<string, object>} communitiesByEcid
 * @property {boolean} billingRelevantOnly
 */

/**
 * Build the billruns energy-data detail table: filter billruns, upsert partial rows across
 * runs, finalize, sort, apply group displays. Returns rows plus unresolved DSO prefixes.
 *
 * @param {BuildDetailRowsInput} input
 * @returns {{ detailRows: object[], dsoWarnings: string[] }}
 */
export function buildDetailRows({
  billruns,
  analysisByBillRunKey,
  interpretationByBillRunKey,
  communitiesByEcid,
  billingRelevantOnly,
}) {
  const dsoWarningSet = new Set();
  const rowMap = new Map();

  for (const billrun of billruns) {
    const key = billRunAnalysisKey(billrun.ecid, billrun.runIdentifier);
    const interpretation = interpretationByBillRunKey[key];
    if (!interpretation || !shouldIncludeBillrun(billrun, interpretation)) {
      continue;
    }
    const analysis = analysisByBillRunKey[key] ?? null;
    const community = communitiesByEcid.get(billrun.ecid) ?? null;
    const partialRows = buildPartialRowsForBillrun(
      billrun,
      analysis,
      community,
      billingRelevantOnly,
      dsoWarningSet
    );
    for (const row of partialRows) {
      upsertPartialRow(rowMap, row);
    }
  }

  const sorted = sortDetailRows(finalizeRows([...rowMap.values()]));
  const detailRows = applyGroupDisplays(sorted);

  return {
    detailRows,
    dsoWarnings: [...dsoWarningSet],
  };
}

/**
 * Serialize detail rows to a semicolon-delimited CSV with dynamic Date1..DateN columns.
 * Uses group-display columns and fixed German problem-type labels.
 *
 * @param {object[]} rows
 * @returns {string}
 */
export function formatDetailRowsForCsv(rows) {
  const maxDateCols = rows.reduce((max, row) => Math.max(max, row.dateRanges?.length ?? 0), 0);
  const dateHeaders = Array.from({ length: maxDateCols }, (_, i) => `Date${i + 1}`);
  const headers = [...CSV_HEADERS_FIXED, ...dateHeaders];

  const data = rows.map((row) => {
    const record = {
      Netzbetreiber: row.dsoDisplay ?? '',
      Gemeinschaft: row.communityDisplay ?? '',
      Zählpunkt: row.meteringPoint ?? '',
      'Problem Typ': CSV_PROBLEM_TYPE_LABELS[row.problemType] ?? row.problemType,
      'Zählpunkt Codes': row.meterCodes ?? '',
      'Last Update': row.lastUpdate ?? '',
    };
    for (let i = 0; i < maxDateCols; i++) {
      record[`Date${i + 1}`] = row.dateRanges?.[i] ?? '';
    }
    return record;
  });

  return Papa.unparse(
    { fields: headers, data },
    {
      quotes: true,
      quoteChar: '"',
      escapeChar: '"',
      delimiter: ';',
      header: true,
      newline: '\r\n',
    }
  );
}

/**
 * Max number of date-range columns across detail rows.
 *
 * @param {object[]} rows
 * @returns {number}
 */
export function getMaxDateRangeCount(rows) {
  return rows.reduce((max, row) => Math.max(max, row.dateRanges?.length ?? 0), 0);
}

/**
 * Filter detail rows by view filters and apply group-display labels.
 *
 * @param {object[]} detailRows
 * @param {string[]} selectedDsoEcns
 * @param {string[]} selectedCommunityEcids
 * @returns {object[]}
 */
export function prepareFilteredDetailRows(
  detailRows,
  selectedDsoEcns,
  selectedCommunityEcids
) {
  const filtered = filterDetailRows(detailRows, selectedDsoEcns, selectedCommunityEcids);
  const stripped = filtered.map((row) => ({
    ...row,
    dsoDisplay: '',
    communityDisplay: '',
  }));
  return applyGroupDisplays(stripped);
}

/**
 * Build a CSV blob and filename for detail rows export.
 *
 * @param {object[]} rows
 * @returns {{ blob: Blob, fileName: string }}
 */
export function exportDetailRowsToCsvBlob(rows) {
  const csvString = formatDetailRowsForCsv(rows);
  const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
  const fileName = `billruns_energy_data_details_${new Date().toISOString().slice(0, 10)}.csv`;
  return { blob, fileName };
}
